<script setup>
// The preview conversation over the draft of the Max's script: what is typed goes to the draft through
// POST scenario_set/preview, with the history kept here (the server keeps none). "Testing as" picks who the
// Max is talking to; changing it starts the conversation again, since the first answers would no longer fit.
// Under each answer, a folded "why" says the step, the attributes and the tools. Nothing is written to the SAP or
// the CRM: the notice of the panel says so.
import { computed, nextTick, ref, watch } from 'vue';
import { useI18n } from 'vue-i18n';
import { useRouter } from 'vue-router';
import { useStore } from 'dashboard/composables/store';
import { Chat16 } from '@carbon/icons-vue';
import { useMessageFormatter } from 'shared/composables/useMessageFormatter';
import PolarisScenarioSet from 'dashboard/api/polaris/scenarioSet';
import MessageBubble from 'dashboard/components-next/polaris/cards/MessageBubble.vue';
import Composer from 'dashboard/components-next/polaris/cards/Composer.vue';
import OptionSelect from 'dashboard/components-next/polaris/cards/OptionSelect.vue';
import {
  Empty,
  EmptyDescription,
  EmptyHeader,
  EmptyMedia,
  EmptyTitle,
} from 'dashboard/components-next/polaris/ui/empty';
import { Button } from 'dashboard/components-next/polaris/ui/button';
import PreviewWhy from './PreviewWhy.vue';
import { TESTING_AS, usePreviewPanel } from './usePreviewPanel';

const props = defineProps({
  assistantId: { type: Number, required: true },
  assistantName: { type: String, default: '' },
});

const { t } = useI18n();
const store = useStore();
const router = useRouter();
const { formatMessage } = useMessageFormatter();
const { testingAs, setTestingAs } = usePreviewPanel();

const messages = ref([]);
const draft = ref('');
const isLoading = ref(false);
// What the server said when the draft could not answer, and the steps its validation points at
const errorMessage = ref('');
const problemSteps = ref([]);
const thread = ref(null);

const nodes = computed(() => store.getters['polarisScenarioSet/getNodes']);
const catalog = computed(() => store.getters['polarisScenarioSet/getCatalog']);

const options = computed(() =>
  TESTING_AS.map(value => ({
    value,
    label: t(`POLARIS.PREVIEW.TESTING_AS.${value.toUpperCase()}`),
  }))
);

const reset = () => {
  messages.value = [];
  draft.value = '';
  errorMessage.value = '';
  problemSteps.value = [];
};
watch(() => props.assistantId, reset);

const scrollToBottom = async () => {
  await nextTick();
  if (thread.value) thread.value.scrollTop = thread.value.scrollHeight;
};
watch(() => [messages.value.length, isLoading.value], scrollToBottom);

const changeTestingAs = value => {
  if (value === testingAs.value) return;
  setTestingAs(value);
  reset();
};

const send = async () => {
  const content = draft.value.trim();
  if (!content || isLoading.value) return;
  messages.value.push({ role: 'user', content });
  draft.value = '';
  errorMessage.value = '';
  problemSteps.value = [];
  isLoading.value = true;
  try {
    const { data } = await PolarisScenarioSet.preview(props.assistantId, {
      messages: messages.value.map(({ role, content: text }) => ({
        role,
        content: text,
      })),
      testingAs: testingAs.value,
    });
    messages.value.push({
      role: 'assistant',
      content: data.reply,
      trace: data.trace,
    });
  } catch (error) {
    const data = error?.response?.data;
    errorMessage.value = data?.error || t('POLARIS.PREVIEW.CHAT.ERROR');
    const found = data?.validation?.errors || [];
    if (found.length) {
      // the steps show their own errors too, and open by themselves
      store.commit(
        'polarisScenarioSet/SET_SCENARIO_VALIDATION',
        data.validation
      );
      problemSteps.value = [...new Set(found.map(item => item.node))]
        .filter(Boolean)
        .map(key => ({
          key,
          title: nodes.value.find(node => node.node_key === key)?.title || key,
        }));
    }
  } finally {
    isLoading.value = false;
  }
};

// Take the person to the step with the problem: on the script page it scrolls there; on another page it goes to
// the script page first.
const goToStep = async key => {
  const find = () => document.querySelector(`[data-scenario-step="${key}"]`);
  if (!find()) {
    await router.push({
      name: 'polaris_assistants_scenarios_index',
      params: { assistantId: props.assistantId },
    });
    await new Promise(resolve => {
      setTimeout(resolve, 400);
    });
  }
  find()?.scrollIntoView({ behavior: 'smooth', block: 'start' });
};

const authorOf = message =>
  message.role === 'user'
    ? t('POLARIS.PLAYGROUND.USER')
    : props.assistantName || t('POLARIS.PLAYGROUND.ASSISTANT');

defineExpose({ reset, hasMessages: computed(() => messages.value.length > 0) });
</script>

<template>
  <div class="flex h-full min-h-0 flex-col gap-3" data-preview-chat>
    <div class="grid shrink-0 gap-1">
      <p class="type-polaris-caption text-polaris-muted-foreground">
        {{ t('POLARIS.PREVIEW.TESTING_AS.LABEL') }}
      </p>
      <OptionSelect
        :model-value="testingAs"
        :options="options"
        :aria-label="t('POLARIS.PREVIEW.TESTING_AS.LABEL')"
        size="sm"
        @update:model-value="changeTestingAs"
      />
    </div>
    <div
      class="flex min-h-0 flex-1 flex-col overflow-hidden rounded-polaris-xl border border-solid border-polaris-border bg-polaris-card"
    >
      <div
        ref="thread"
        class="flex min-h-0 flex-1 flex-col gap-4 overflow-y-auto p-3"
        aria-live="polite"
      >
        <Empty v-if="!messages.length" class="flex-1 border-0">
          <EmptyHeader>
            <EmptyMedia variant="icon">
              <Chat16 aria-hidden="true" />
            </EmptyMedia>
            <EmptyTitle>{{ t('POLARIS.PREVIEW.CHAT.EMPTY_TITLE') }}</EmptyTitle>
            <EmptyDescription>
              {{ t('POLARIS.PREVIEW.CHAT.EMPTY_DESCRIPTION') }}
            </EmptyDescription>
          </EmptyHeader>
        </Empty>
        <div
          v-for="(message, index) in messages"
          :key="index"
          class="grid gap-2"
        >
          <MessageBubble
            :from="message.role === 'user' ? 'customer' : 'agent'"
            :author="authorOf(message)"
          >
            <div
              class="break-words [&_p]:m-0"
              v-html="formatMessage(message.content)"
            />
          </MessageBubble>
          <PreviewWhy
            v-if="message.trace"
            :trace="message.trace"
            :steps="nodes"
            :fact-labels="catalog.facts"
            :tool-labels="catalog.tools"
          />
        </div>
        <MessageBubble
          v-if="isLoading"
          from="agent"
          :author="assistantName || t('POLARIS.PLAYGROUND.ASSISTANT')"
        >
          <span
            class="flex gap-1 py-1"
            :aria-label="t('POLARIS.PAGES.PLAYGROUND.TYPING')"
            role="status"
          >
            <span
              class="size-2 animate-bounce rounded-full bg-current motion-reduce:animate-none"
            />
            <span
              class="size-2 animate-bounce rounded-full bg-current motion-reduce:animate-none [animation-delay:0.2s]"
            />
            <span
              class="size-2 animate-bounce rounded-full bg-current motion-reduce:animate-none [animation-delay:0.4s]"
            />
          </span>
        </MessageBubble>
        <div
          v-if="errorMessage"
          class="grid gap-2 type-polaris-caption text-polaris-destructive"
          role="alert"
          data-preview-error
        >
          <p>{{ errorMessage }}</p>
          <ul v-if="problemSteps.length" class="m-0 grid list-none gap-1 p-0">
            <li v-for="step in problemSteps" :key="step.key">
              <Button
                type="button"
                variant="link"
                size="xs"
                class="h-auto p-0"
                data-preview-problem-step
                @click="goToStep(step.key)"
              >
                {{
                  t('POLARIS.PREVIEW.CHAT.GO_TO_STEP', { title: step.title })
                }}
              </Button>
            </li>
          </ul>
        </div>
      </div>
      <div class="shrink-0 p-3 pt-0">
        <Composer
          v-model="draft"
          compact
          :rows="2"
          :label="t('POLARIS.PLAYGROUND.MESSAGE_PLACEHOLDER')"
          :submit-label="t('POLARIS.PAGES.PLAYGROUND.SEND')"
          :placeholder="t('POLARIS.PLAYGROUND.MESSAGE_PLACEHOLDER')"
          @submit="send"
        />
      </div>
    </div>
  </div>
</template>
